/**
 * Feature flags - Single source of truth
 * @typedef {'about-us' | 'career-page' | 'header-search' | 'job-market' | 'price-offer' | 'price-offer-landing' | 'price-offer-public-access' | 'blog' | 'questions' | 'professionals' | 'favourites' | 'prices' | 'inspiration' | 'salary-checker' | 'registration' | 'customer-registration' | 'google-login' | 'dynamic-nav-links' | 'bank-statement-import'} FeatureKey
 */

/**
 * Available feature flags
 * This is the single source of truth for all feature constants
 * @type {Record<string, FeatureKey>}
 */
export const FEATURES = Object.freeze({
  ABOUT_US: "about-us",
  CAREER_PAGE: "career-page",
  HEADER_SEARCH: "header-search",
  JOB_MARKET: "job-market",
  PRICE_OFFER: "price-offer",
  PRICE_OFFER_LANDING: "price-offer-landing",
  PRICE_OFFER_PUBLIC_ACCESS: "price-offer-public-access",
  BLOG: "blog",
  QUESTIONS: "questions",
  PROFESSIONALS: "professionals",
  FAVOURITES: "favourites",
  PRICES: "prices",
  INSPIRATION: "inspiration",
  SALARY_CHECKER: "salary-checker",
  REGISTRATION: "registration",
  CUSTOMER_REGISTRATION: "customer-registration",
  GOOGLE_LOGIN: "google-login",
  DYNAMIC_NAV_LINKS: "dynamic-nav-links",
  BANK_STATEMENT_IMPORT: "bank-statement-import",
});

/**
 * Feature flags configuration for routes
 * Maps route names to their required features
 *
 * Route names are the custom names defined in page components (e.g., name: 'blogLandingPage')
 * These names get locale suffixes from i18n (e.g., 'blogLandingPage___hu')
 * The getRouteFeature() function automatically strips the locale suffix for matching
 *
 * Features should be defined in /branding/*.json files
 * Routes will only be accessible if the current brand includes the required feature
 */

export const routeFeatures = {
  // Job Market Features - Tender/Job Management
  requestForTender: FEATURES.JOB_MARKET,
  requestForTenderTab: FEATURES.JOB_MARKET,
  specifyTenderPage: FEATURES.JOB_MARKET,
  closedTendersPage: FEATURES.JOB_MARKET,
  wonTendersPage: FEATURES.JOB_MARKET,
  boughtTendersPage: FEATURES.JOB_MARKET,
  buyTendersPage: FEATURES.JOB_MARKET,
  myJobPage: FEATURES.JOB_MARKET,
  jobNotificationPage: FEATURES.JOB_MARKET,

  // Job Market - Conductor/Admin
  conductorTendersIndexPage: FEATURES.JOB_MARKET,
  conductorTendersListIndexPage: FEATURES.JOB_MARKET,
  conductorTendersCreatePage: FEATURES.JOB_MARKET,
  conductorTendersEditPage: FEATURES.JOB_MARKET,
  conductorTendersJudgeIndexPage: FEATURES.JOB_MARKET,
  conductorJobFeedsIndexPage: FEATURES.JOB_MARKET,
  conductorJobFeedsViewPage: FEATURES.JOB_MARKET,
  conductorLeadsIndexPage: FEATURES.JOB_MARKET,
  conductorLeadsViewPage: FEATURES.JOB_MARKET,

  // Price Offer Features
  priceOfferLandingPage: FEATURES.PRICE_OFFER_LANDING,
  publicPriceOfferPage: FEATURES.PRICE_OFFER,
  priceOfferRegistrationPage: FEATURES.PRICE_OFFER,
  myPriceOffersIndexPage: FEATURES.PRICE_OFFER,
  priceOffersEditPage: FEATURES.PRICE_OFFER,
  priceOffersCreatePage: FEATURES.PRICE_OFFER,

  // Product Catalog
  productCatalogPage: FEATURES.PRICE_OFFER,

  // Price Offer Features - Demo
  demoPriceOffersIndexPage: FEATURES.PRICE_OFFER_PUBLIC_ACCESS,
  demoPriceOffersEditPage: FEATURES.PRICE_OFFER_PUBLIC_ACCESS,
  demoPriceOffersCreatePage: FEATURES.PRICE_OFFER_PUBLIC_ACCESS,
  demoPublicPriceOfferPage: FEATURES.PRICE_OFFER_PUBLIC_ACCESS,

  // Favourites Features
  favourites: FEATURES.FAVOURITES,
  conductorPersonFavouritesIndexPage: FEATURES.FAVOURITES,

  // Blog Features
  blogLandingPage: FEATURES.BLOG,
  blogArticlePage: FEATURES.BLOG,
  blogListPageByTag: FEATURES.BLOG,
  blogListPageByCollection: FEATURES.BLOG,
  blogListPageByProfession: FEATURES.BLOG,
  blogListPageByProfessionTask: FEATURES.BLOG,

  // Blog - Conductor/Admin
  conductorBlogIndexPage: FEATURES.BLOG,
  conductorBlogArticlesIndexPage: FEATURES.BLOG,
  conductorBlogArticlesCreatePage: FEATURES.BLOG,
  conductorBlogArticlesEditPage: FEATURES.BLOG,
  conductorBlogCollectionsIndexPage: FEATURES.BLOG,
  conductorBlogCollectionsCreatePage: FEATURES.BLOG,
  conductorBlogCollectionsEditPage: FEATURES.BLOG,
  conductorBlogTagsIndexPage: FEATURES.BLOG,
  conductorBlogTagsCreatePage: FEATURES.BLOG,
  conductorBlogTagsEditPage: FEATURES.BLOG,

  // Questions Features
  questionsPage: FEATURES.QUESTIONS,
  questionPage: FEATURES.QUESTIONS,
  addQuestionPage: FEATURES.QUESTIONS,
  professionQuestions: FEATURES.QUESTIONS,
  professionTaskQuestions: FEATURES.QUESTIONS,

  // Questions - Conductor/Admin
  conductorQuestionsIndexPage: FEATURES.QUESTIONS,
  conductorQuestionEditPage: FEATURES.QUESTIONS,

  // Professionals Features
  professionals: FEATURES.PROFESSIONALS,
  professionalPage: FEATURES.PROFESSIONALS,
  professionalRatingPage: FEATURES.PROFESSIONALS,
  newProfessionalsPage: FEATURES.PROFESSIONALS,

  // Inspiration Features
  inspirationLandingPage: FEATURES.INSPIRATION,
  inspirationPage: FEATURES.INSPIRATION,
  inspirationPicturePage: FEATURES.INSPIRATION,
  inspirationProfessionPage: FEATURES.INSPIRATION,
  inspirationProfessionTaskPage: FEATURES.INSPIRATION,

  // Inspiration - Conductor/Admin
  conductorInspirationsIndexPage: FEATURES.INSPIRATION,
  conductorInspirationsCreatePage: FEATURES.INSPIRATION,
  conductorInspirationsEditPage: FEATURES.INSPIRATION,

  // Salary Checker Features
  salariesPage: FEATURES.SALARY_CHECKER,
  salaryCheckerPage: FEATURES.SALARY_CHECKER,
  salaryCheckerAveragePage: FEATURES.SALARY_CHECKER,
  salaryCheckerJobsPage: FEATURES.SALARY_CHECKER,

  // Registration Features
  registrationPage: FEATURES.REGISTRATION,
  RegistrationRedirect: FEATURES.REGISTRATION,
  registrationJobfeed: FEATURES.REGISTRATION,
  registrationStepEmail: FEATURES.REGISTRATION,
  registrationStepPassword: FEATURES.REGISTRATION,
  registrationStepCity: FEATURES.REGISTRATION,
  registrationStepProfession: FEATURES.REGISTRATION,
  registrationStepPhone: FEATURES.REGISTRATION,
  registrationStepName: FEATURES.REGISTRATION,
  registrationStepPicture: FEATURES.REGISTRATION,
  registrationStepIntroduction: FEATURES.REGISTRATION,
  registrationStepInvoice: FEATURES.REGISTRATION,
  registrationStepSuccess: FEATURES.REGISTRATION,

  // Customer Registration Features
  customerRegistrationPage: FEATURES.CUSTOMER_REGISTRATION,

  // OAuth Registration
  oauthRegisterPage: FEATURES.GOOGLE_LOGIN,

  careerPage: FEATURES.CAREER_PAGE,
  aboutUs: FEATURES.ABOUT_US,

  conductorFinanceTransactionsImportPage: FEATURES.BANK_STATEMENT_IMPORT,
};
